import { supabase } from '@/integrations/supabase/client';
import { ensureFreshSession } from '@/lib/authSession';

export function assertSnapshotActive(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Avatar snapshot cancelled', 'AbortError');
}

export function isSnapshotCancelled(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export async function assertSnapshotSession(userId: string, signal?: AbortSignal): Promise<void> {
  assertSnapshotActive(signal);
  if (!(await ensureFreshSession())) throw new DOMException('Avatar session unavailable', 'AbortError');
  assertSnapshotActive(signal);
  const { data: { session } } = await supabase.auth.getSession();
  assertSnapshotActive(signal);
  if (session?.user.id !== userId) throw new DOMException('Avatar session changed', 'AbortError');
}

// Binds a saved preview to its source GLB without putting the model URL in logs
// or query strings. Old unbound snapshot.png images are regenerated once.
async function sourceKey(glbUrl: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(glbUrl));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function snapshotMatchesAvatar(snapshotUrl: string, glbUrl: string): Promise<boolean> {
  try {
    const name = new URL(snapshotUrl).pathname.split('/').pop() ?? '';
    return name.startsWith(`${await sourceKey(glbUrl)}-`) && name.endsWith('.png');
  } catch { return false; }
}

export function isRetryableSnapshotError(error: unknown): boolean {
  if (isSnapshotCancelled(error)) return false;
  const status = Number((error as { statusCode?: unknown; status?: unknown })?.statusCode
    ?? (error as { status?: unknown })?.status);
  return status === 408 || status === 429 || status >= 500
    || (error instanceof TypeError && /fetch|network/i.test(error.message));
}

/** INSERT-only immutable upload: deployed avatar policies deliberately have no
 * general SELECT/list permission. upsert:true requires SELECT + UPDATE, even on
 * a public bucket. Do not broaden RLS to save a derived thumbnail.
 */
export async function saveAvatarSnapshot(
  blob: Blob, glbUrl: string, userId: string, signal?: AbortSignal,
): Promise<string> {
  await assertSnapshotSession(userId, signal);
  const { data: profile, error: readError } = await supabase.from('user_profiles')
    .select('avatar_url, avatar_snapshot_url').eq('user_id', userId).maybeSingle();
  assertSnapshotActive(signal);
  if (readError) throw readError;
  if (!profile || profile.avatar_url !== glbUrl) {
    throw new DOMException('Avatar selection changed', 'AbortError');
  }
  if (profile.avatar_snapshot_url && await snapshotMatchesAvatar(profile.avatar_snapshot_url, glbUrl)) {
    assertSnapshotActive(signal);
    return profile.avatar_snapshot_url;
  }

  const previousSnapshot = profile.avatar_snapshot_url;
  const path = `${userId}/snapshots/${await sourceKey(glbUrl)}-${crypto.randomUUID()}.png`;
  const bucket = supabase.storage.from('avatars');
  let uploaded = false;
  try {
    await assertSnapshotSession(userId, signal);
    const { error } = await bucket.upload(path, blob, {
      contentType: 'image/png', cacheControl: '31536000', upsert: false,
    });
    if (error) throw error;
    uploaded = true;
    await assertSnapshotSession(userId, signal);
    const url = bucket.getPublicUrl(path).data.publicUrl;
    if (!url) throw new Error('Avatar snapshot URL unavailable');

    // Compare both source and previous snapshot: a late render may neither
    // replace the newly selected avatar's image nor overwrite another render.
    let update = supabase.from('user_profiles').update({ avatar_snapshot_url: url })
      .eq('user_id', userId).eq('avatar_url', glbUrl);
    update = previousSnapshot == null
      ? update.is('avatar_snapshot_url', null)
      : update.eq('avatar_snapshot_url', previousSnapshot);
    // Once dispatched, a network failure has an uncertain outcome. Keep the
    // object rather than risk removing an image the server already linked.
    uploaded = false;
    const { data: saved, error: saveError } = await update.select('avatar_snapshot_url').maybeSingle();
    if (saveError) throw saveError;
    if (!saved) {
      uploaded = true; // confirmed compare-and-set miss: object is unpublished
      throw new DOMException('Avatar snapshot superseded', 'AbortError');
    }
    // The durable write succeeded. Do not remove its object if cancellation
    // happened during the request; the source/CAS predicates protected it.
    uploaded = false;
    assertSnapshotActive(signal);
    return saved.avatar_snapshot_url ?? url;
  } finally {
    // Only our newly inserted, unpublished object can be removed here.
    // Never remove the previous image or an uncertain successful profile write.
    if (uploaded) {
      try { await bucket.remove([path]); } catch { /* best-effort orphan cleanup */ }
    }
  }
}
